import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Badge, Button, Spinner } from 'reactstrap';

import {
  type EmbeddingJob,
  type EmbeddingRuntimeLogEvent,
} from '@/api/cigar-matching';
import Icon from '@/components/icon';

import { useEmbeddingRuntimeLogs } from './use-embedding-runtime-logs';

type LogFilter = 'all' | 'warn' | 'error';

const getApiErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Worker logs could not be loaded.';

const formatLogTimestamp = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatVisibleLogs = (events: EmbeddingRuntimeLogEvent[]) =>
  events
    .map(
      (event) =>
        `${formatLogTimestamp(event.timestamp)} [${event.level.toUpperCase()}] ${event.message}`
    )
    .join('\n');

export function EmbeddingRuntimeLogsPanel({
  job,
  enabled = true,
}: {
  job: EmbeddingJob;
  enabled?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [lookbackMinutes, setLookbackMinutes] = useState(30);
  const [filter, setFilter] = useState<LogFilter>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [copyLabel, setCopyLabel] = useState('Copy visible logs');
  const consoleRef = useRef<HTMLDivElement | null>(null);
  const nearBottomRef = useRef(true);
  const active = enabled && expanded;
  const { events, response, isLoading, error, refresh } =
    useEmbeddingRuntimeLogs({
      jobId: job.id,
      status: job.status,
      expanded: active,
      lookbackMinutes,
    });

  useEffect(() => {
    setExpanded(false);
    setFilter('all');
    setCopyLabel('Copy visible logs');
    nearBottomRef.current = true;
  }, [job.id]);

  useEffect(() => {
    if (!enabled) setExpanded(false);
  }, [enabled]);

  const visibleEvents = useMemo(
    () =>
      events.filter((event) =>
        filter === 'all' ? true : event.level === filter
      ),
    [events, filter]
  );
  const warningCount = events.filter((event) => event.level === 'warn').length;
  const errorCount = events.filter((event) => event.level === 'error').length;

  useLayoutEffect(() => {
    const consoleElement = consoleRef.current;
    if (!consoleElement || !autoScroll || !nearBottomRef.current) return;
    consoleElement.scrollTop = consoleElement.scrollHeight;
  }, [autoScroll, visibleEvents]);

  const handleScroll = () => {
    const consoleElement = consoleRef.current;
    if (!consoleElement) return;
    nearBottomRef.current =
      consoleElement.scrollHeight -
        consoleElement.scrollTop -
        consoleElement.clientHeight <
      48;
  };

  const copyLogs = async () => {
    try {
      await navigator.clipboard.writeText(formatVisibleLogs(visibleEvents));
      setCopyLabel('Copied');
      window.setTimeout(() => setCopyLabel('Copy visible logs'), 1500);
    } catch {
      setCopyLabel('Copy failed');
      window.setTimeout(() => setCopyLabel('Copy visible logs'), 1500);
    }
  };

  return (
    <section className="recognition-runtime-logs">
      <button
        type="button"
        className="recognition-runtime-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        <span>
          <Icon name="terminal" size={14} />
          <strong>Worker Console</strong>
          {!expanded && warningCount > 0 && (
            <Badge color="warning">{warningCount} warnings</Badge>
          )}
          {!expanded && errorCount > 0 && (
            <Badge color="danger">{errorCount} errors</Badge>
          )}
        </span>
        <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={14} />
      </button>

      {expanded && (
        <div className="recognition-runtime-body">
          <div className="recognition-runtime-controls">
            <div className="recognition-runtime-filters" aria-label="Log level">
              {(['all', 'warn', 'error'] as LogFilter[]).map((level) => (
                <button
                  type="button"
                  key={level}
                  className={filter === level ? 'active' : undefined}
                  aria-pressed={filter === level}
                  onClick={() => setFilter(level)}
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {level === 'all'
                    ? 'All'
                    : level === 'warn'
                      ? 'Warnings'
                      : 'Errors'}
                </button>
              ))}
            </div>
            <label>
              Lookback
              <select
                aria-label="Runtime log lookback"
                value={lookbackMinutes}
                onChange={(event) =>
                  setLookbackMinutes(Number(event.target.value))
                }
              >
                <option value={30}>30 minutes</option>
                <option value={60}>60 minutes</option>
                <option value={180}>180 minutes</option>
              </select>
            </label>
            <label className="recognition-runtime-autoscroll">
              <input
                type="checkbox"
                checked={autoScroll}
                onChange={(event) => setAutoScroll(event.target.checked)}
              />
              Auto-scroll
            </label>
            <Button
              color="light"
              size="sm"
              disabled={isLoading}
              onClick={refresh}
            >
              {isLoading ? (
                <Spinner size="sm" />
              ) : (
                <Icon name="refresh-cw" size={13} />
              )}
              Refresh
            </Button>
            <Button
              color="light"
              size="sm"
              disabled={!visibleEvents.length}
              onClick={copyLogs}
            >
              {copyLabel}
            </Button>
          </div>

          {error && (
            <div className="recognition-runtime-error" role="alert">
              <strong>Unable to load worker logs.</strong>
              <span>{getApiErrorMessage(error)}</span>
            </div>
          )}

          {!error && response && !response.available && (
            <div className="recognition-runtime-empty">
              Worker logs are not available yet.
            </div>
          )}

          {!error && response?.available && !visibleEvents.length && (
            <div className="recognition-runtime-empty">
              {events.length
                ? 'No logs match this level filter.'
                : 'No worker logs found in this time window.'}
            </div>
          )}

          {!error && !response && isLoading && (
            <div className="recognition-runtime-empty">
              <Spinner size="sm" /> Loading worker logs…
            </div>
          )}

          {visibleEvents.length > 0 && (
            <div
              ref={consoleRef}
              className="recognition-runtime-console"
              onScroll={handleScroll}
              role="log"
              aria-live="off"
            >
              {visibleEvents.map((event) => (
                <div
                  key={event.id}
                  className={`recognition-runtime-event recognition-runtime-event-${event.level}`}
                  data-level={event.level}
                >
                  <time dateTime={event.timestamp}>
                    {formatLogTimestamp(event.timestamp)}
                  </time>
                  <span className="recognition-runtime-level">
                    {event.level === 'warn'
                      ? 'WARN'
                      : event.level.toUpperCase()}
                  </span>
                  <pre>{event.message}</pre>
                </div>
              ))}
            </div>
          )}

          {response?.truncated && (
            <div className="recognition-runtime-truncated">
              Results truncated. Narrow the lookback or refresh for the latest
              events.
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default EmbeddingRuntimeLogsPanel;
